import { SlashCommand, SlashCommandApp } from '@@types/Api/Command';
import { CHANNEL_ID, COMMAND_ID, SLASH_COMMAND_APP_ID, StringTMap } from '@@types/common';
import { Options } from '@popperjs/core';

import { PopperType, PROFILE_TYPE, URLS } from '~/constants';
import { MINUTE } from '~/constants/date';
import { uiState } from '~/stores/UiState';
import { then } from '~/utils/monadUtil';
import { isEmpty } from 'lodash';
import { action, comparer, computed, observable } from 'mobx';
import {
  __,
  ascend,
  assoc,
  descend,
  dissoc,
  findIndex,
  flatten,
  has,
  mapObjIndexed,
  mergeDeepRight,
  pipe,
  pluck,
  prop,
  propEq,
  reduce,
  reject,
  sortWith,
  tap,
  uniqBy,
  values,
} from 'ramda';

import { api } from './providers/Api2';

class SlashCommandAppsStore {
  @observable
  public activeUserAppId = '';

  @observable
  public registrableApps: StringTMap<SlashCommandApp> = {};

  @observable
  channelApps: StringTMap<SlashCommandApp[]> = {};

  public meetingTriggeredChannel: string | null = null;

  @observable
  private userApps: StringTMap<SlashCommandApp> = {};

  @observable
  private apps = {};

  @observable
  private profileAppId: string | null = null;

  commandTimestampMap: StringTMap<CHANNEL_ID> = {};

  setCommandTimestamp = (timestamp: number, channelId: CHANNEL_ID) => {
    const pivot = Date.now() - MINUTE;
    Object.keys(this.commandTimestampMap).forEach((value) => {
      if (pivot - Number(value) < 0) {
        delete this.commandTimestampMap[value];
      }
    });
    this.commandTimestampMap[timestamp] = channelId;
  };

  @computed
  get userAppList(): SlashCommandApp[] {
    return pipe<StringTMap<SlashCommandApp>, SlashCommandApp[], SlashCommandApp[]>(
      values,
      sortWith([descend(prop('createdAt'))])
    )(this.userApps);
  }

  @computed
  get activeUserApp() {
    return this.userApps[this.activeUserAppId];
  }

  @computed
  public get profileApp() {
    return this.allApps[this.profileAppId];
  }

  @action
  public setApp = (app: SlashCommandApp) => {
    this.apps[app.id] = app;
  };

  @computed({ equals: comparer.shallow })
  get channelCommands(): StringTMap<SlashCommand[]> {
    return (
      mapObjIndexed(
        pipe<SlashCommandApp[], SlashCommandApp[], SlashCommand[][], SlashCommand[], SlashCommand[]>(
          sortWith<any>([ascend(prop('order')), descend(has('order'))]),
          pluck('commands'),
          flatten,
          uniqBy((command) => command.id)
        ),
        this.channelApps
      ) ?? {}
    );
  }

  @computed({ equals: comparer.shallow })
  private get allChannelApps(): StringTMap<SlashCommandApp> {
    return pipe<any, any, any, any>(
      values,
      flatten,
      reduce((acc: StringTMap<SlashCommandApp>, app: SlashCommandApp) => {
        acc[app.id] = app;
        return acc;
      }, {})
    )(this.channelApps);
  }

  @computed({ equals: comparer.shallow })
  private get allChannelCommands(): StringTMap<SlashCommand> {
    return this.toCommands(this.allChannelApps);
  }

  @computed({ equals: comparer.shallow })
  private get allApps(): StringTMap<SlashCommandApp> {
    return Object.assign({}, this.registrableApps, this.apps, this.allChannelApps);
  }

  @computed({ equals: comparer.shallow })
  public get allCommands(): StringTMap<SlashCommand> {
    return this.toCommands(this.allApps);
  }

  private toCommands = (apps: StringTMap<SlashCommandApp>): StringTMap<SlashCommand> => {
    return (Object.values(apps) ?? []).reduce((acc, app: SlashCommandApp) => {
      return (
        app.commands?.reduce((acc, command: SlashCommand) => {
          acc[command.id] = command;
          return acc;
        }, acc) ?? acc
      );
    }, {});
  };

  @action
  public setUserApps = (apps: StringTMap<SlashCommandApp>) => {
    this.userApps = apps;
  };

  @action
  public addUserApp = (app: SlashCommandApp) => {
    this.userApps[app.id] = app;
  };

  @action
  public updateUserApp = (app: Partial<SlashCommandApp>) => {
    this.userApps[app.id] = Object.assign({}, this.userApps[app.id], app);
  };

  public setUserAppCommands = (appId: SLASH_COMMAND_APP_ID, commands: SlashCommand[]) => {
    this.updateUserApp({ id: appId, commands });
  };

  @action
  public setToken = (appId, token) => {
    this.userApps[appId].token = token;
  };

  public updateCommand = (command: SlashCommand) => {
    const commands = this.userApps[command.integrationAppId].commands;
    const newCommands = commands.map((_command) => {
      return command.id === _command.id ? Object.assign({}, _command, command) : _command;
    });
    this.setUserAppCommands(command.integrationAppId, newCommands);
  };

  public getAppById = (appId: string): SlashCommandApp => {
    return this.allApps[appId];
  };

  public addUserAppCommand = (command: SlashCommand) => {
    const commands = this.userApps[command.integrationAppId].commands;
    this.setUserAppCommands(command.integrationAppId, [...commands, command]);
  };

  public deleteUserAppCommand = (command) => {
    const commands = this.userApps[command.integrationAppId].commands;
    const newCommands = reject(propEq('id', command.id), commands);
    this.setUserAppCommands(command.integrationAppId, newCommands);
  };

  public getIconUrl = (appId) => {
    const app = this.getAppById(appId);
    if (!app) {
      return URLS.DEFAULT_BOT_IMAGE;
    }
    return app && app.iconAttachId && app.iconAttachId !== '0'
      ? `${URLS.API}/apps/${app.id}/icon/${app.iconAttachId}`
      : URLS.DEFAULT_BOT_IMAGE;
  };

  @action
  public selectAppExceptAppId = (appId) => {
    const userApp = this.userAppList.find(({ id }) => {
      return appId !== id;
    });
    this.setActiveUserAppId(userApp?.id ?? '');
  };

  @action
  public deleteUserApp = (appId) => {
    delete this.userApps[appId];
  };

  @action
  public setCommandAccessType = (command) => {
    this.updateCommand(command);
    const app = this.userApps[command.integrationAppId];
    const index = findIndex(propEq('id', command.id), app.commands);
    app.commands[index].accessType = command.accessType;
  };

  @action
  public updateUserAppCommand = (command) => {
    const app = this.userApps[command.integrationAppId];
    const index = findIndex(propEq('id', command.id), app.commands);
    app.commands[index] = mergeDeepRight(app.commands[index], command);
  };

  public getUserAppById = (appId: string): SlashCommandApp => {
    return this.userApps[appId];
  };

  @action
  public setActiveUserAppId = (appId: string) => {
    this.activeUserAppId = appId;
  };

  @action
  public setRegistrableApps = (apps: StringTMap<SlashCommandApp>) => {
    this.registrableApps = apps;
  };

  @action
  public setProfileAppId = (appId: string | null) => {
    this.profileAppId = appId;
  };

  @action
  setChannelApps = (channelId, apps) => {
    this.channelApps = uiState.pickAndSetLruChannelIds(channelId, apps)(this.channelApps);
  };

  public getChannelCommandByName = (name: string, channelId: CHANNEL_ID) => {
    return this.channelCommands[channelId]?.find(propEq('name', name));
  };

  public getChannelCommandById = (commandId: COMMAND_ID, channelId: CHANNEL_ID) => {
    return this.channelCommands[channelId]?.find(propEq('id', commandId));
  };

  public hasCommandByNameInChannel = (name: string, channelId: CHANNEL_ID): boolean => {
    return !!this.getChannelCommandByName(name, channelId);
  };

  public hasCommandByIdInChannel = (commandId: string, channelId: CHANNEL_ID): boolean => {
    return !!this.getChannelCommandById(commandId, channelId);
  };

  public setMeetingTriggeredChannel(channelId: string | null = null) {
    this.meetingTriggeredChannel = channelId;

    if (!channelId) {
      return;
    }
    // 1분 유예 후 자동열기 안되게 수정
    window.setTimeout(() => {
      if (this.meetingTriggeredChannel === channelId) {
        this.meetingTriggeredChannel = null;
      }
    }, MINUTE);
  }

  fetchPromises: Record<string, Promise<SlashCommandApp>> = {};

  fetchAppById = (appId: string): Promise<SlashCommandApp> => {
    const fetchPromise = this.fetchPromises[appId] || api.commandApp.fetchAppById(appId);
    return pipe<any, Promise<SlashCommandApp>, Promise<SlashCommandApp>, Promise<SlashCommandApp>>(
      tap(assoc(appId, __, this.fetchPromises)),
      then(tap(() => dissoc(appId, this.fetchPromises))),
      then(tap(assoc(appId, __, this.apps)))
    )(fetchPromise);
  };

  getOrFetchApp = (appId: SLASH_COMMAND_APP_ID) => {
    const app = this.allApps[appId];
    if (app) {
      return Promise.resolve(app);
    }

    return this.fetchAppById(appId);
  };

  openAppProfilePopper = async ({
    referenceNode,
    options,
    appId,
  }: {
    referenceNode: HTMLElement;
    options: Partial<Options>;
    appId: SLASH_COMMAND_APP_ID;
  }) => {
    const app = await this.getOrFetchApp(appId);
    if (isEmpty(app)) return;
    this.setProfileAppId(appId);
    uiState.openPopper({
      name: PopperType.Profile,
      args: { referenceNode, options, type: PROFILE_TYPE.APP },
    });
  };
}

export default SlashCommandAppsStore;

export const slashCommandAppsStore = new SlashCommandAppsStore();
